import Icon from '@cobliteam/ui/icons';
import { AdvancedMarker, AdvancedMarkerAnchorPoint } from '@vis.gl/react-google-maps';
import { useCallback, useMemo } from 'react';

interface MapTrackingMarkersProps {
  basePolylineStartEnd: { start: google.maps.LatLng; end: google.maps.LatLng } | null;
  highlightPolylineStartEnd: { start: google.maps.LatLng; end: google.maps.LatLng } | null;
}

/**
 * Renders markers for the start and end points of tracking paths
 * Shows both base path points (small circles) and highlighted path points (A/B markers)
 */
const MapTrackingMarkers = ({ basePolylineStartEnd, highlightPolylineStartEnd }: MapTrackingMarkersProps) => {
  /**
   * Converts a LatLng object to a position object or returns null if invalid
   */
  const getMarkerPosition = useCallback((point?: google.maps.LatLng) => {
    if (!point) return null;
    return { lat: point.lat(), lng: point.lng() };
  }, []);

  // Generate position objects for base path markers
  const baseMarkers = useMemo(() => {
    if (!basePolylineStartEnd) return [];

    return [getMarkerPosition(basePolylineStartEnd.start), getMarkerPosition(basePolylineStartEnd.end)];
  }, [basePolylineStartEnd, getMarkerPosition]);

  // Generate position objects for highlighted path markers
  const highlightMarkers = useMemo(() => {
    if (!highlightPolylineStartEnd) return [];

    return [getMarkerPosition(highlightPolylineStartEnd.start), getMarkerPosition(highlightPolylineStartEnd.end)];
  }, [highlightPolylineStartEnd, getMarkerPosition]);

  return (
    <>
      {/* Render base path markers (start and end points) */}
      {baseMarkers.map((marker) =>
        marker ? (
          <AdvancedMarker key={`${marker.lat},${marker.lng}`} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} position={marker} zIndex={1}>
            <Icon name='circle' color='black' className='!size-2 fill-black' />
          </AdvancedMarker>
        ) : null
      )}

      {/* Render highlight path markers (A/B indicators) */}
      {highlightMarkers.map((marker, index) =>
        marker ? (
          <AdvancedMarker key={`${marker.lat},${marker.lng}`} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} position={marker} zIndex={3}>
            <div className='flex size-6 items-center justify-center rounded-full border-4 bg-primary font-bold text-white shadow'>{index === 0 ? 'A' : 'B'}</div>
          </AdvancedMarker>
        ) : null
      )}
    </>
  );
};

export default MapTrackingMarkers;
